// =============================================================================
// MeuDriver — Booking Step 1: Date / Time / Modality
// =============================================================================

import { useState } from 'react'
import { ChevronRight, RepeatIcon } from 'lucide-react'
import { format, addMinutes } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useBookingStore } from '@/store/bookingStore'
import { formatCurrency } from '@/lib/utils'
import {
  DURATION_OPTIONS,
  PACKAGE_OPTIONS,
  TRIAL_DURATION_MINUTES,
  TRIAL_PRICE_MULTIPLIER,
} from '@/lib/constants'
import type { InstructorWithProfile } from '@/types/database'
import { InstructorSummaryCard } from './BookingFlowShared'
import { SlotPicker } from '@/components/student/SlotPicker'
import { RecurringBookingForm } from '@/components/student/RecurringBookingForm'

const MODE_OPTIONS = [
  { key: 'single'  as const, label: 'Aula avulsa',       desc: 'Uma aula por vez' },
  { key: 'trial'   as const, label: 'Aula experimental', desc: '30 min · 50% de desconto' },
  { key: 'package' as const, label: 'Pacote',            desc: 'Compre mais, economize mais' },
]

interface Props {
  instructor: InstructorWithProfile
  onNext: () => void
}

export function BookingStep1DateTime({ instructor, onNext }: Props) {
  const store = useBookingStore()
  const navigate = useNavigate()
  const [showRecurring, setShowRecurring] = useState(false)
  const hourlyRate = instructor.hourly_rate ?? 0

  const isTrial = store.bookingMode === 'trial'
  const isPackage = store.bookingMode === 'package'

  const activeDuration = isTrial ? TRIAL_DURATION_MINUTES : store.durationMinutes
  const selectedPkg = PACKAGE_OPTIONS.find((p) => p.quantity === store.packageQuantity) ?? PACKAGE_OPTIONS[1]

  const unitSubtotal = hourlyRate * (activeDuration / 60) * (isTrial ? TRIAL_PRICE_MULTIPLIER : 1)
  const pkgSubtotal  = isPackage
    ? unitSubtotal * selectedPkg.quantity * (1 - selectedPkg.discountPercent / 100)
    : null

  const canProceed = Boolean(store.selectedDate && store.selectedTime)

  return (
    <div className="space-y-6">
      <InstructorSummaryCard instructor={instructor} />

      {/* Toggle recorrente */}
      <button
        type="button"
        onClick={() => setShowRecurring((prev) => !prev)}
        className={`flex items-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-all w-full sm:w-auto ${
          showRecurring
            ? 'border-brand-500 bg-brand-50 text-brand-700'
            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
        }`}
        data-testid="toggle-recurring-btn"
      >
        <RepeatIcon className="h-4 w-4" />
        Agendar recorrente (série de aulas)
      </button>

      {/* Formulário recorrente */}
      {showRecurring && (
        <Card className="border-brand-200 bg-brand-50/30">
          <CardContent className="p-5">
            <RecurringBookingForm
              instructorId={instructor.id}
              bookingType="single"
              onSuccess={(_seriesId, _count) => {
                navigate('/aluno/dashboard')
              }}
              onCancel={() => setShowRecurring(false)}
            />
          </CardContent>
        </Card>
      )}

      {/* Conteúdo normal — esconde se modo recorrente ativo */}
      {!showRecurring && (
        <>

      {/* Booking mode selector */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">Tipo de aula</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {MODE_OPTIONS.map((mode) => {
            const isSelected = store.bookingMode === mode.key
            return (
              <button
                key={mode.key}
                type="button"
                onClick={() => {
                  store.setBookingMode(mode.key)
                  if (mode.key === 'trial') store.setDuration(TRIAL_DURATION_MINUTES)
                  else if (store.durationMinutes === TRIAL_DURATION_MINUTES) store.setDuration(60)
                }}
                className={`rounded-xl border-2 p-3 text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <p className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                  {mode.label}
                  {mode.key === 'trial' && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">
                      50% OFF
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{mode.desc}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* Package quantity picker */}
      {isPackage && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Escolha o pacote</Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {PACKAGE_OPTIONS.map((pkg) => {
              const isSelected = store.packageQuantity === pkg.quantity
              const pkgTotal = hourlyRate * (store.durationMinutes / 60) * pkg.quantity * (1 - pkg.discountPercent / 100)
              return (
                <button
                  key={pkg.quantity}
                  type="button"
                  onClick={() => store.setPackageQuantity(pkg.quantity)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <p className={`text-sm font-bold ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                      {pkg.quantity} aulas
                    </p>
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-bold text-green-700">
                      {pkg.badge}
                    </span>
                  </div>
                  <p className="mt-1.5 text-base font-extrabold text-gray-900">
                    {formatCurrency(pkgTotal)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(pkgTotal / pkg.quantity)}/aula
                  </p>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {isTrial && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-green-800 mb-1">Aula experimental — 30 minutos</p>
            <p className="text-xs text-green-700">
              Conheça o instrutor com 50% de desconto. Duração fixa de 30 min.
              Preço: {formatCurrency(unitSubtotal)}.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Seleção de data e horário via disponibilidade real do instrutor */}
      <SlotPicker instructorId={instructor.id} />

      {store.selectedTime && !isTrial && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Duração da aula</Label>
          <div className="flex flex-wrap gap-3">
            {DURATION_OPTIONS.map((opt) => {
              const isSelected = store.durationMinutes === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => store.setDuration(opt.value)}
                  className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors
                    ${isSelected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
                    }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          {store.selectedDate && store.selectedTime && (
            <p className="text-sm text-muted-foreground">
              Término previsto: {(() => {
                try {
                  const start = new Date(`${format(store.selectedDate, 'yyyy-MM-dd')}T${store.selectedTime}:00`)
                  return format(addMinutes(start, store.durationMinutes), "HH'h'mm", { locale: ptBR })
                } catch { return '' }
              })()}
            </p>
          )}
        </div>
      )}

      {store.selectedTime && (
        <Card className="bg-gray-50">
          <CardContent className="p-4 flex justify-between items-center text-sm">
            <span className="text-muted-foreground">
              {isPackage
                ? `${selectedPkg.quantity} aulas · ${selectedPkg.discountPercent}% de desconto`
                : isTrial
                ? '30 min · 50% de desconto'
                : `${store.durationMinutes / 60}h × ${formatCurrency(hourlyRate)}/h`}
            </span>
            <span className="text-lg font-extrabold text-primary">
              {formatCurrency(isPackage ? pkgSubtotal! : unitSubtotal)}
            </span>
          </CardContent>
        </Card>
      )}

      <div className="pt-2">
        <Button size="lg" className="w-full sm:w-auto" disabled={!canProceed} onClick={onNext}>
          Próximo
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      </>
      )}
    </div>
  )
}
